/**
 * Hook para gerenciar módulo ativo do tenant (Conversas ou Negócios/CRM)
 * 
 * O estado é persistido no localStorage para manter a preferência
 * entre sessões e navegações.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createLogger } from '@/lib/logger';
import { useAuth } from '@/hooks/useAuth';

// Logger para módulo ativo
const logger = createLogger('ActiveModule');

// Tipos de módulo disponíveis
// 'activities' = novo módulo de Atividades (Fase 3 — Playbook de Vendas)
export type ModuleType = 'conversations' | 'crm' | 'financial' | 'tasks' | 'referral' | 'activities';

/**
 * Gera chave de localStorage com escopo por usuário.
 * Isola dados entre diferentes usuários no mesmo navegador.
 * 
 * @param base - Nome base da chave (ex: 'activeModule')
 * @param userId - ID do usuário autenticado
 * @returns Chave com escopo por usuário (ex: 'activeModule_abc123')
 */
function scopedKey(base: string, userId: string | null): string {
  return userId ? `${base}_${userId}` : base;
}

/** Rotas padrão de fallback quando não há dados salvos */
const DEFAULT_LAST_ROUTES: Record<ModuleType, string> = {
  conversations: '/tenant',
  crm: '/tenant/crm',
  financial: '/tenant/financial',
  tasks: '/tenant/tasks',
  referral: '/tenant/indicacao',
  activities: '/tenant/activities/list',
};

/**
 * Obtém as últimas rotas salvas por módulo do localStorage (com escopo por usuário)
 */
function getLastRoutes(userId: string | null): Record<ModuleType, string> {
  try {
    const saved = localStorage.getItem(scopedKey('moduleLastRoutes', userId));
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    logger.warn('Erro ao recuperar últimas rotas', { error: e });
  }
  return { ...DEFAULT_LAST_ROUTES };
}

/**
 * Salva a última rota de um módulo no localStorage (com escopo por usuário)
 */
function saveLastRoute(module: ModuleType, route: string, userId: string | null): void {
  try {
    const current = getLastRoutes(userId);
    current[module] = route;
    localStorage.setItem(scopedKey('moduleLastRoutes', userId), JSON.stringify(current));
    logger.debug('Última rota salva', { module, route });
  } catch (e) {
    logger.warn('Erro ao salvar última rota', { error: e });
  }
}

// Rotas que pertencem ao módulo CRM
// OBS: rotas migradas para a sidebar de Atividades (Feed Atividades, Agenda,
// Agendamento Online e Playbook de Vendas) NÃO entram aqui — elas são tratadas
// como SHARED_ROUTES para preservar o módulo ativo (CRM ou Atividades).
const CRM_ROUTES = [
  '/tenant/crm',
  '/tenant/crm/workspaces',
  '/tenant/crm/pipelines',
  '/tenant/crm/deals',
  '/tenant/crm/products',
  '/tenant/crm/settings',
];

// Rotas que pertencem ao módulo Financeiro
const FINANCIAL_ROUTES = [
  '/tenant/financial',
];

// Rotas que pertencem ao módulo Tarefas
const TASKS_ROUTES = [
  '/tenant/tasks',
];

// Rotas que pertencem ao módulo Indicação (Fase 5 - Bloco 5A)
const REFERRAL_ROUTES = [
  '/tenant/indicacao',
];

// Rotas que pertencem ao novo módulo Atividades (Fase 3 — Playbook de Vendas)
// Padronizado sob /tenant/activities/* (legacy /activities/* segue como redirect).
const ACTIVITIES_ROUTES = [
  '/tenant/activities',
];

// Rotas compartilhadas entre módulos (não alteram o módulo ativo)
// Exportada para reutilização em ModuleSwitcher e App.tsx
export const SHARED_ROUTES = [
  '/tenant/contacts',
  '/tenant/lists',
  '/tenant/flows',                    // Fluxos acessível de todos os módulos
  '/tenant/settings',                 // Configurações acessível de todos os módulos
  '/tenant/distribution',             // Filas de distribuição - acessível de todos os módulos
  '/tenant/crm/customers',            // Clientes - compartilhado com Financeiro
  '/tenant/crm/deals',                // Todos os Negócios - compartilhado com Financeiro
  '/tenant/crm/generated-contracts',  // Contratos Gerados - compartilhado com Financeiro
  '/tenant/crm/categories',           // Categorias - compartilhado com Financeiro
  '/tenant/crm/products',             // Produtos - compartilhado com Financeiro
  '/tenant/crm/price-tables',         // Tabelas de Preço - compartilhado com Financeiro
  // Permissões Dashboard (rota antiga) — mantida apenas como redirect em App.tsx.
  // A central unificada abaixo é a rota viva; ambas precisam ser SHARED para
  // não trocar o módulo ativo enquanto o usuário está nessas telas.
  '/tenant/crm/dashboard-permissions',
  '/tenant/permissions',               // Central unificada de Permissões (v1.0.0) — preserva módulo ativo
  '/tenant/crm/playbooks',             // Playbook de Vendas - acessível a partir de CRM e Atividades
  '/tenant/crm/workspaces',            // Workspaces - acessível de CRM e Atividades
  '/tenant/crm/activities',            // Feed Atividades - acessível de CRM e Atividades
  '/tenant/scheduling',                // Agendamento Online - acessível de CRM e Atividades
  '/tenant/activities/list',           // Listagem Atividades - acessível de CRM e Atividades
  '/tenant/activities/dashboard'       // Dashboard de Atividades - acessível de CRM e Atividades
];

/**
 * Verifica se um pathname é uma rota compartilhada entre módulos
 * Helper exportado para uso em ModuleSwitcher e outros componentes
 */
export function isSharedModuleRoute(pathname: string): boolean {
  return SHARED_ROUTES.some(route => pathname.startsWith(route));
}

// =========================================================================
// Rotas/Helpers especiais do CRM
// =========================================================================

// Prefixo da rota de Atividades do CRM.
// Atividades é acessível via menu do topo, mas "CRM" deve sempre levar
// para uma tela "principal" do CRM, nunca ficar "preso" em /activities.
const CRM_ACTIVITIES_PREFIX = '/tenant/crm/activities';

/**
 * Verifica se o pathname pertence à rota de Atividades do CRM.
 * Usamos startsWith para cobrir subrotas (ex.: /tenant/crm/activities/123).
 */
function isCrmActivitiesRoute(pathname: string): boolean {
  return pathname.startsWith(CRM_ACTIVITIES_PREFIX);
}

// Rota padrão de cada módulo
const MODULE_DEFAULT_ROUTES: Record<ModuleType, string> = {
  conversations: '/tenant',
  crm: '/tenant/crm',
  financial: '/tenant/financial',
  tasks: '/tenant/tasks',
  referral: '/tenant/indicacao',
  activities: '/tenant/activities/list',
};

/**
 * Interface de retorno do hook
 */
interface UseActiveModuleReturn {
  /** Módulo atualmente ativo */
  activeModule: ModuleType;
  /** Alterna para um módulo específico */
  switchModule: (module: ModuleType) => void;
  /** Verifica se o módulo CRM está disponível para o tenant */
  isCrmAvailable: boolean;
  /** Verifica se o módulo Financeiro está disponível para o tenant */
  isFinancialAvailable: boolean;
  /** Verifica se o módulo Tarefas está disponível para o tenant */
  isTasksAvailable: boolean;
  /** Verifica se o módulo Indicação está disponível para o tenant */
  isReferralAvailable: boolean;
  /** Indica se está carregando dados de disponibilidade */
  isLoading: boolean;
}

/**
 * Hook para gerenciar módulo ativo do tenant
 * @param crmEnabled - Se o módulo CRM está habilitado para o tenant
 */
export function useActiveModule(crmEnabled: boolean = false, financialEnabled: boolean = false, tasksEnabled: boolean = false, referralEnabled: boolean = false): UseActiveModuleReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Referência ao userId para uso em funções de localStorage
  const userIdRef = useRef<string | null>(user?.id ?? null);
  userIdRef.current = user?.id ?? null;

  // Chave de localStorage com escopo por usuário
  const storageKey = scopedKey('activeModule', user?.id ?? null);
  
  // Estado do módulo ativo
  const [activeModule, setActiveModule] = useState<ModuleType>(() => {
    const saved = localStorage.getItem(storageKey) as ModuleType | null;
    
    // Rotas compartilhadas: manter módulo salvo no localStorage (não forçar troca)
    if (SHARED_ROUTES.some(route => location.pathname.startsWith(route))) {
      return saved || 'conversations';
    }
    
    // Detectar módulo pela rota atual e sincronizar localStorage imediatamente
    if (ACTIVITIES_ROUTES.some(route => location.pathname.startsWith(route))) {
      localStorage.setItem(storageKey, 'activities');
      return 'activities';
    }
    if (REFERRAL_ROUTES.some(route => location.pathname.startsWith(route))) {
      localStorage.setItem(storageKey, 'referral');
      return 'referral';
    }
    if (TASKS_ROUTES.some(route => location.pathname.startsWith(route))) {
      localStorage.setItem(storageKey, 'tasks');
      return 'tasks';
    }
    if (FINANCIAL_ROUTES.some(route => location.pathname.startsWith(route))) {
      localStorage.setItem(storageKey, 'financial');
      return 'financial';
    }
    if (CRM_ROUTES.some(route => location.pathname.startsWith(route))) {
      localStorage.setItem(storageKey, 'crm');
      return 'crm';
    }
    
    return saved || 'conversations';
  });
  
  const [isLoading, setIsLoading] = useState(false);

  // Sincronizar módulo ativo com a rota atual
  useEffect(() => {
    const isCrmRoute = CRM_ROUTES.some(route => location.pathname.startsWith(route));
    const isFinancialRoute = FINANCIAL_ROUTES.some(route => location.pathname.startsWith(route));
    const isTasksRoute = TASKS_ROUTES.some(route => location.pathname.startsWith(route));
    const isReferralRoute = REFERRAL_ROUTES.some(route => location.pathname.startsWith(route));
    const isActivitiesRoute = ACTIVITIES_ROUTES.some(route => location.pathname.startsWith(route));
    const isSharedRoute = SHARED_ROUTES.some(route => location.pathname.startsWith(route));
    
    if (isSharedRoute) {
      logger.debug('Rota compartilhada - mantendo módulo ativo', { path: location.pathname, activeModule });
      return;
    }
    
    let detectedModule: ModuleType = 'conversations';
    if (isActivitiesRoute) detectedModule = 'activities';
    else if (isReferralRoute) detectedModule = 'referral';
    else if (isTasksRoute) detectedModule = 'tasks';
    else if (isFinancialRoute) detectedModule = 'financial';
    else if (isCrmRoute) detectedModule = 'crm';
    
    if (detectedModule !== activeModule) {
      setActiveModule(detectedModule);
      localStorage.setItem(storageKey, detectedModule);
      logger.debug('Módulo alterado baseado na rota', { path: location.pathname, module: detectedModule });
    }
  }, [location.pathname, activeModule, storageKey]);

  // Salvar última rota visitada do módulo ativo (exceto rotas compartilhadas)
  useEffect(() => {
    const isSharedRoute = SHARED_ROUTES.some(route => location.pathname.startsWith(route));
    if (isSharedRoute) return;
    
    const isCrmRoute = CRM_ROUTES.some(route => location.pathname.startsWith(route));
    const isFinancialRoute = FINANCIAL_ROUTES.some(route => location.pathname.startsWith(route));
    const isTasksRoute = TASKS_ROUTES.some(route => location.pathname.startsWith(route));
    const isReferralRoute = REFERRAL_ROUTES.some(route => location.pathname.startsWith(route));
    const isActivitiesRoute = ACTIVITIES_ROUTES.some(route => location.pathname.startsWith(route));
    const currentModule: ModuleType = isActivitiesRoute ? 'activities' : isReferralRoute ? 'referral' : isTasksRoute ? 'tasks' : isFinancialRoute ? 'financial' : isCrmRoute ? 'crm' : 'conversations';

    if (currentModule === 'crm' && isCrmActivitiesRoute(location.pathname)) {
      logger.debug('Ignorando Atividades como última rota do CRM', { path: location.pathname });
      return;
    }

    saveLastRoute(currentModule, location.pathname, userIdRef.current);
  }, [location.pathname]);

  /**
   * Alterna para um módulo específico e navega para última rota salva
   */
  const switchModule = useCallback((module: ModuleType) => {
    const currentPath = location.pathname;

    // Verificar se está na página de atividades (caso especial)
    const isActivitiesPage = isCrmActivitiesRoute(currentPath);

    // Verifica se está em rota compartilhada
    const isOnSharedRoute = SHARED_ROUTES.some(route => currentPath.startsWith(route));

    // Se já está no módulo E não está em Atividades E não está em rota compartilhada, não fazer nada
    if (module === activeModule && !isActivitiesPage && !isOnSharedRoute) return;

    // Se tentar acessar CRM sem estar habilitado, ignorar
    if (module === 'crm' && !crmEnabled) {
      logger.warn('Tentativa de acessar CRM sem permissão');
      return;
    }

    // Se tentar acessar Financeiro sem estar habilitado, ignorar
    if (module === 'financial' && !financialEnabled) {
      logger.warn('Tentativa de acessar Financeiro sem permissão');
      return;
    }
    // Se tentar acessar Tarefas sem estar habilitado, ignorar
    if (module === 'tasks' && !tasksEnabled) {
      logger.warn('Tentativa de acessar Tarefas sem permissão');
      return;
    }

    logger.info('Alternando módulo', { from: activeModule, to: module });

    setActiveModule(module);
    localStorage.setItem(storageKey, module);

    // Navegar para última rota salva do módulo (com escopo por usuário)
    const lastRoutes = getLastRoutes(userIdRef.current);
    let targetRoute = lastRoutes[module] || MODULE_DEFAULT_ROUTES[module];

    // Fallback robusto: se o histórico anterior salvou Atividades como última rota do CRM,
    // forçar navegação para a rota principal do CRM.
    if (module === 'crm' && isCrmActivitiesRoute(targetRoute)) {
      logger.debug('Fallback: última rota do CRM aponta para Atividades', { targetRoute });
      targetRoute = MODULE_DEFAULT_ROUTES.crm;
    }

    // Segurança extra: se ainda assim estiver tentando navegar para a mesma rota atual,
    // forçar o fallback (evita sensação de "não aconteceu nada").
    if (module === 'crm' && isActivitiesPage && targetRoute === currentPath) {
      targetRoute = MODULE_DEFAULT_ROUTES.crm;
    }

    logger.debug('Navegando para última rota do módulo', { module, targetRoute });
    navigate(targetRoute);
  }, [activeModule, crmEnabled, financialEnabled, tasksEnabled, navigate, location.pathname, storageKey]);

  return {
    activeModule,
    switchModule,
    isCrmAvailable: crmEnabled,
    isFinancialAvailable: financialEnabled,
    isTasksAvailable: tasksEnabled,
    isReferralAvailable: referralEnabled,
    isLoading
  };
}
